/**
 * OrgVerificationRow — sysadmin affordance for L4E organization verification
 * (POLICY_ACCOUNT_SECURITY_LADDER rule 4, DEV-1439). Community Settings →
 * Basics, below the guidelines editor. Verification gates invitation/roster
 * issuance; it flows down the org family (a verified parent covers
 * sub-orgs — shown as "inherited" here).
 */

import React, { useCallback, useEffect, useState } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import {
  fetchOrgVerification,
  setOrgVerification,
  type OrgVerificationResponse,
} from "../../api/orgVerification";
import styles from "./CommunityGuidelinesEditor.module.css";

interface Props {
  communityGuid: string;
  /** Toggle renders only for sysadmins (server enforces regardless).
   *  Non-sysadmin settings viewers (org admins) see READ-ONLY status —
   *  so they understand whether their org can issue invitations
   *  (Skip, 2026-07-08). */
  isSystemAdmin: boolean;
  /** Only org-typed communities can be verified. */
  isOrgCommunity: boolean;
}

const OrgVerificationRow: React.FC<Props> = ({
  communityGuid,
  isSystemAdmin,
  isOrgCommunity,
}) => {
  const { t } = useAppTranslation("community");
  const [state, setState] = useState<OrgVerificationResponse | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!isOrgCommunity) return;
    try {
      setState(await fetchOrgVerification(communityGuid));
    } catch {
      setState(null);
    }
  }, [communityGuid, isOrgCommunity]);

  useEffect(() => {
    load();
  }, [load]);

  if (!isOrgCommunity || !state) return null;

  const toggle = async () => {
    setBusy(true);
    try {
      setState(await setOrgVerification(communityGuid, !state.verifiedDirectly));
    } catch (e) {
      window.alert(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const statusText = state.verifiedDirectly
    ? t("settings.orgVerification.verified", {
        date: state.verifiedAt
          ? new Date(state.verifiedAt).toLocaleDateString()
          : "",
      })
    : state.effectivelyVerified
      ? t("settings.orgVerification.inherited")
      : t("settings.orgVerification.notVerified");

  return (
    <div className={styles.wrap}>
      <div className={styles.labelRow}>
        <label className={styles.label}>{t("settings.orgVerification.title")}</label>
        <span className={styles.version}>{statusText}</span>
      </div>
      <p className={styles.hint}>{t("settings.orgVerification.hint")}</p>
      {/* Toggle is sysadmin-only; org admins get the read-only status above. */}
      {isSystemAdmin && (
        <div className={styles.actions}>
          <button
            type="button"
            className={state.verifiedDirectly ? styles.clearBtn : styles.saveBtn}
            disabled={busy}
            onClick={toggle}
          >
            {busy
              ? t("settings.guidelines.saving")
              : state.verifiedDirectly
                ? t("settings.orgVerification.unverify")
                : t("settings.orgVerification.verify")}
          </button>
        </div>
      )}
    </div>
  );
};

export default OrgVerificationRow;
